import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { isFeatureEnabled } from '../config/app'
import { clearAnonymousUserData } from '../utils/storageManager'
import { clearCart } from '../store/slices/cartSlice'

export interface OAuthCallbackResult {
  isProcessing: boolean
  isSuccess: boolean
  error: string | null
  user: any | null
  resetOAuthState: () => void
}

export const useOAuthCallback = (): OAuthCallbackResult => {
  const dispatch = useDispatch()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [user, setUser] = useState<any | null>(null)

  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Only process OAuth callback if Cognito Google OAuth is enabled
      if (!isFeatureEnabled('enableCognitoGoogleOAuth')) {
        return
      }

      // Check if we're returning from an OAuth redirect
      const urlParams = new URLSearchParams(window.location.search)
      const hasCode = urlParams.has('code')
      const hasState = urlParams.has('state')
      const hasError = urlParams.has('error')

      // If there's an error parameter, handle it
      if (hasError) {
        const errorDescription = urlParams.get('error_description') || 'OAuth authentication failed'
        setError(errorDescription)
        setIsProcessing(false)
        return
      }

      // If we have OAuth parameters, process the callback
      if (hasCode && hasState) {
        setIsProcessing(true)
        setError(null)

        try {
          const { fetchUserAttributes, getCurrentUser, fetchAuthSession } = await import(
            'aws-amplify/auth'
          )
          const currentUser = await getCurrentUser()

          // Try to fetch Cognito session tokens and persist them for API auth
          let tokens: { accessToken?: string; idToken?: string } = {}
          try {
            const session = await fetchAuthSession()
            const accessToken = session.tokens?.accessToken?.toString()
            const idToken = session.tokens?.idToken?.toString()

            if (accessToken) localStorage.setItem('accessToken', accessToken)
            if (idToken) localStorage.setItem('idToken', idToken)
            if (currentUser?.username) localStorage.setItem('username', currentUser.username)

            tokens = { accessToken, idToken }
          } catch (_) {
            // Non-blocking if session tokens are not available
          }

          // Clear anonymous user data (cart, wishlist, etc.) on successful OAuth login
          clearAnonymousUserData()
          dispatch(clearCart())
          
          const userData = {
            username: currentUser.username,
            isAuthenticated: true,
            attributes: {
              sub: currentUser.userId,
              email: currentUser.signInDetails?.loginId,
            },
            tokens,
          }

          setUser(userData)
          setIsSuccess(true)

          const newUrl = window.location.origin + window.location.pathname
          window.history.replaceState({}, document.title, newUrl)

          try {
            const attributes = await fetchUserAttributes()
            setUser((prevUser: any) => ({
              ...prevUser,
              attributes: attributes,
            }))
          } catch (attrError: any) {
            // Continue with basic user info - don't log NotAuthorizedException as it's expected
            if (attrError.name !== 'NotAuthorizedException') {
              // Could not fetch user attributes
            }
          }
        } catch (err: any) {
          if (err.name !== 'UserUnAuthenticatedException') {
            setError(err.message || 'Failed to complete OAuth authentication')
            setIsSuccess(false)
          }
        } finally {
          setIsProcessing(false)
        }
      }
    }

    handleOAuthCallback()
  }, [dispatch])

  const resetOAuthState = () => {
    setIsProcessing(false)
    setIsSuccess(false)
    setError(null)
    setUser(null)
  }

  return {
    isProcessing,
    isSuccess,
    error,
    user,
    resetOAuthState,
  }
}
